/**
 * ProcedureTimeline.tsx
 * ─────────────────────
 * Sidebar sticky — chronologie + articles visés + jurisprudence liée.
 *
 * Fix v2 :
 *   - mergeCitations filtre par number ET par numéro extrait du title
 *   - formatDate gère les dates texte françaises ("2 juillet 2020")
 *   - Rendu "SIMILAIRE" affiche number + solution proprement
 *   - Citations LLM affichent la solution de l'arrêt cité
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { getDateLocale } from '@/i18n';
import { articlePathFromFiche, articleLabel } from '@/utils/articleSlug';
import type { ProcedureLink } from './types';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface TimelineEntry {
  role:             'contested' | 'timeline' | 'current' | 'forward';
  source:           'judilibre' | 'lineage' | 'qdrant';
  judilibre_id:     string | null;
  date:             string | null;
  title:            string;
  jurisdiction:     string;
  location:         string;
  chamber:          string;
  solution:         string;
  number:           string | null;
  description:      string;
  ongoing:          boolean;
  partial:          boolean;
  smartlawyer_id:   string | null;
  smartlawyer_slug: string | null;
}

interface TimelineData {
  has_judilibre:  boolean;
  judilibre_id:   string | null;
  timeline:       TimelineEntry[];
  rapprochements: TimelineEntry[];
  summary:        string;
  visa:           VisaItem[];
}

interface PublicFicheArticle {
  code:       string;
  article:    string;
  node_id?:   string | null;
  slug_code?: string | null;
}

interface CiteArretQualifie {
  ref:            string;
  type:           string;
  found?:         boolean;
  fiche_id?:      string | null;
  slug?:          string | null;
  decision_date?: string | null;
  chamber?:       string | null;
  solution?:      string | null;
  probleme?:      string | null;
}

interface Props {
  ficheId:           string;
  fiche?:            {
    id?:       string;
    articles?: PublicFicheArticle[];
  } | null;
  relations?:        {
    articles?:              PublicFicheArticle[];
    cite_arrets_qualifies?: CiteArretQualifie[];
    procedure_graph?:       ProcedureLink[];
    fiche_id?:              string;
  } | null;
  relationsLoading?: boolean;
}

// ── Config visuelle ───────────────────────────────────────────────────────────

const ROLE_CONFIG: Record<string, { dotClass: string; lineClass: string }> = {
  contested: {
    dotClass:  'bg-slate-400 dark:bg-slate-500 border-slate-300 dark:border-slate-600',
    lineClass: 'border-slate-200 dark:border-slate-700',
  },
  timeline: {
    dotClass:  'bg-blue-400 dark:bg-blue-500 border-blue-300 dark:border-blue-700',
    lineClass: 'border-blue-100 dark:border-blue-900',
  },
  current: {
    dotClass:  'bg-primary border-primary/50 ring-4 ring-primary/10',
    lineClass: 'border-primary/20',
  },
  forward: {
    dotClass:  'bg-emerald-400 dark:bg-emerald-500 border-emerald-300 dark:border-emerald-700',
    lineClass: 'border-emerald-100 dark:border-emerald-900',
  },
};

// labelKey : clé i18n à passer à t() au rendu
const CITE_TYPE_LABEL: Record<string, { labelKey: string; color: string }> = {
  revirement:                  { labelKey: 'fiche.timeline.cite.revirement',    color: 'text-red-600 dark:text-red-400' },
  confirmation:                { labelKey: 'fiche.timeline.cite.confirmation',  color: 'text-emerald-600 dark:text-emerald-400' },
  application_principe:        { labelKey: 'fiche.timeline.cite.application_principe',   color: 'text-blue-600 dark:text-blue-400' },
  distinction:                 { labelKey: 'fiche.timeline.cite.distinction',   color: 'text-purple-600 dark:text-purple-400' },
  reference_jurisprudentielle: { labelKey: 'fiche.timeline.cite.reference_jurisprudentielle',     color: 'text-indigo-600 dark:text-indigo-400' },
  meme_affaire:                { labelKey: 'fiche.timeline.cite.meme_affaire',  color: 'text-amber-600 dark:text-amber-400' },
  obiter_dictum:               { labelKey: 'fiche.timeline.cite.obiter_dictum', color: 'text-muted-foreground' },
};

function solutionColor(solution: string): string {
  const key = (solution || '').toLowerCase();
  if (key.includes('cassation')) return 'text-red-600 dark:text-red-400';
  if (key.includes('rejet'))     return 'text-emerald-600 dark:text-emerald-400';
  return 'text-muted-foreground';
}

// ── formatDate — gère les dates ISO et les dates texte françaises ─────────────

const FR_MONTHS: Record<string, string> = {
  janvier: '01', février: '02', fevrier: '02', mars: '03',
  avril: '04', mai: '05', juin: '06', juillet: '07',
  août: '08', aout: '08', septembre: '09', octobre: '10',
  novembre: '11', décembre: '12', decembre: '12',
};

function formatDate(date: string | null): string {
  if (!date) return '';

  // Date texte française : "2 juillet 2020" ou "19 novembre 2020"
  if (/[a-zA-ZÀ-ÿ]/.test(date)) {
    const m = date.match(/(\d{1,2})\s+([\wÀ-ÿ]+)\s+(\d{4})/i);
    if (m) {
      const month = FR_MONTHS[m[2].toLowerCase()];
      if (month) {
        try {
          return new Date(`${m[3]}-${month}-${m[1].padStart(2, '0')}`).toLocaleDateString(getDateLocale(), {
            day: '2-digit', month: 'short', year: 'numeric',
          });
        } catch {
          return date;
        }
      }
    }
    return date;
  }

  // Date ISO : "2020-11-19" ou "2020-11-19T..."
  try {
    return new Date(date).toLocaleDateString(getDateLocale(), {
      day: '2-digit', month: 'short', year: 'numeric',
    });
  } catch {
    return date;
  }
}

function stripHtml(html: string): string {
  return html.replace(/<[^>]+>/g, '').trim();
}

// ── Mapping link_type → libellé lisible ───────────────────────────────────────
// Clés i18n fiche.timeline.link.*
const LINK_TYPE_LABELS = new Set([
  'reference_jurisprudentielle', 'rapprochement', 'cassation_annulation', 'renvoi',
  'appel', 'appel_contestation', 'premiere_instance', 'cassation_rejet',
  'infirmation', 'confirmation', 'revirement', 'procedure_anterieure',
]);

function humanizeTitle(title: string | null | undefined, t: TFunction): string {
  if (!title) return '—';
  return LINK_TYPE_LABELS.has(title) ? t(`fiche.timeline.link.${title}`) : title;
}

// ── Normalisation numéro pour comparaison ─────────────────────────────────────

function normRef(s: string | null | undefined): string {
  return (s || '').toLowerCase().replace(/[\s.-]/g, '');
}

// Extrait le numéro de pourvoi depuis un title Judilibre brut
const _POURVOI_IN_TITLE = /pourvoi\s+n[°o°]\s*(\d{2}-\d{2,3}[.-]\d{3,4})/i;

function numFromTitle(title: string | null | undefined): string {
  const m = _POURVOI_IN_TITLE.exec(title || '');
  return m ? m[1] : '';
}

// ── Skeletons ─────────────────────────────────────────────────────────────────

function SkeletonBlock({ rows = 3, title = true }: { rows?: number; title?: boolean }) {
  return (
    <div className="rounded-xl border bg-card p-4 animate-pulse">
      {title && <div className="h-2 bg-muted rounded w-1/3 mb-3" />}
      <div className="space-y-2">
        {Array.from({ length: rows }).map((_, i) => (
          <div key={i} className="h-2 bg-muted rounded" style={{ width: `${60 + (i % 3) * 12}%` }} />
        ))}
      </div>
    </div>
  );
}

// ── TimelineNode ──────────────────────────────────────────────────────────────

function TimelineNode({ entry, isLast }: { entry: TimelineEntry; isLast: boolean }) {
  const { t } = useTranslation();
  const cfg       = ROLE_CONFIG[entry.role] ?? ROLE_CONFIG.timeline;
  const isCurrent = entry.role === 'current';

  const content = (
    <div className={`
      relative pl-3 py-2.5 pr-2 rounded-lg transition-all duration-150
      ${isCurrent
        ? 'bg-primary/5 border border-primary/20'
        : entry.smartlawyer_id
          ? 'hover:bg-muted/60 cursor-pointer'
          : entry.partial
            ? 'opacity-50 italic'
            : 'opacity-80'
      }
    `}>
      {isCurrent && (
        <span className="absolute -top-2 left-3 text-[9px] font-bold uppercase tracking-widest text-primary bg-background px-1.5">
          {t('fiche.timeline.this_decision')}
        </span>
      )}
      {entry.date && (
        <p className="text-[10px] font-mono text-muted-foreground/70 mb-0.5">
          {formatDate(entry.date)}
        </p>
      )}
      <p className={`text-xs font-semibold leading-tight ${isCurrent ? 'text-primary' : 'text-foreground'}`}>
        {entry.location || entry.jurisdiction || humanizeTitle(entry.title, t)}
      </p>
      {entry.chamber && (
        <p className="text-[10px] text-muted-foreground mt-0.5 leading-tight">{entry.chamber}</p>
      )}
      {entry.number && (
        <p className="text-[10px] font-mono text-muted-foreground/60 mt-0.5">{entry.number}</p>
      )}
      {entry.solution && (
        <p className={`text-[10px] font-medium mt-1 ${solutionColor(entry.solution)}`}>
          {entry.solution}
        </p>
      )}
      {entry.ongoing && (
        <span className="inline-flex items-center gap-1 mt-1 text-[9px] text-amber-600 dark:text-amber-400 font-medium">
          <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
          {t('fiche.timeline.ongoing')}
        </span>
      )}
      {entry.description && entry.role === 'forward' && (
        <p className="text-[10px] text-muted-foreground/70 mt-1 italic leading-tight line-clamp-2">
          {entry.description}
        </p>
      )}
      {entry.partial && !entry.number && (
        <p className="text-[9px] text-muted-foreground/40 mt-1 italic">{t('fiche.timeline.unnumbered')}</p>
      )}
      {!entry.smartlawyer_id && !isCurrent && !entry.partial && (
        <p className="text-[9px] text-muted-foreground/40 mt-1">{t('fiche.timeline.not_indexed')}</p>
      )}
    </div>
  );

  return (
    <div className="flex gap-2.5">
      <div className="flex flex-col items-center shrink-0 w-4">
        <div className={`w-3 h-3 rounded-full border-2 shrink-0 mt-3 ${cfg.dotClass}`} />
        {!isLast && (
          <div className={`w-px flex-1 border-l-2 border-dashed mt-1 ${cfg.lineClass}`} />
        )}
      </div>
      <div className="flex-1 pb-3 min-w-0">
        {entry.smartlawyer_slug && !isCurrent ? (
          <a href={`/jurisprudence/${entry.smartlawyer_slug}`} target="_blank" rel="noopener noreferrer">
            {content}
          </a>
        ) : content}
      </div>
    </div>
  );
}

// ── Merge articles ────────────────────────────────────────────────────────────

function normalizeArticleNum(n: string): string {
  return n.toLowerCase()
    .replace(/^[lr]\.?\s*/, '')
    .replace(/[\s]/g, '');
}

function extractArticleNums(text: string): string[] {
  const matches = text.match(/[lr]\.?\s*\d+[-/]\d+|[lr]\.?\s*\d+|\d+[-/]\d+|\b\d{3,}\b/gi) || [];
  return matches.map(normalizeArticleNum).filter(Boolean);
}

type VisaItem = { title: string; url?: string | null; numero?: string | null; code_text?: string | null };

function mergeArticles(
  ficheArticles:     PublicFicheArticle[],
  relationsArticles: PublicFicheArticle[],
  visaJudilibre:     VisaItem[],
): { articles: PublicFicheArticle[]; visaOnly: VisaItem[] } {
  const seen = new Set<string>();
  const smArticles: PublicFicheArticle[] = [];
  for (const a of [...(ficheArticles || []), ...(relationsArticles || [])]) {
    const normalizedArticle = normalizeArticleNum(a.article);
    const key = `${a.code.toLowerCase().replace(/\s/g, '')}|${normalizedArticle}`;
    if (!seen.has(key)) { seen.add(key); smArticles.push(a); }
  }
  const smArticleNums = new Set(smArticles.map(a => normalizeArticleNum(a.article)));
  const visaOnly = (visaJudilibre || []).filter(v => {
    const text = stripHtml(v.title).toLowerCase();
    const nums = extractArticleNums(text);
    return nums.length === 0 || !nums.some(n => smArticleNums.has(n));
  });
  return { articles: smArticles, visaOnly };
}

// ── Merge citations ───────────────────────────────────────────────────────────

function mergeCitations(
  ourCitations:            CiteArretQualifie[],
  judilibreRapprochements: TimelineEntry[],
): { ours: CiteArretQualifie[]; judilibreOnly: TimelineEntry[] } {
  // Set des refs LLM normalisées
  const ourRefs = new Set(
    (ourCitations || []).map(c => normRef(c.ref))
  );

  const judilibreOnly = (judilibreRapprochements || []).filter(r => {
    // Essai 1 : number direct (renseigné par le backend fix)
    const num = normRef(r.number);
    if (num && ourRefs.has(num)) return false;
    // Essai 2 : numéro extrait du title (fallback si number encore null)
    const numTitle = normRef(numFromTitle(r.title));
    if (numTitle && ourRefs.has(numTitle)) return false;
    return true;
  });

  return {
    ours:          (ourCitations || []).slice(0, 5),
    judilibreOnly: judilibreOnly.slice(0, 3),
  };
}

// ── Composant principal ───────────────────────────────────────────────────────

export function ProcedureTimeline({
  ficheId,
  fiche,
  relations,
  relationsLoading = false,
}: Props) {
  const { t } = useTranslation();
  const [data, setData]           = useState<TimelineData | null>(null);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(false);
  const [showAllArticles, setShowAllArticles] = useState(false);

  useEffect(() => {
    if (!ficheId) return;
    setLoading(true); setError(false); setData(null);
    fetch(`/api/fiches/${ficheId}/timeline`)
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(setData)
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [ficheId]);

  if (loading) return (
    <aside className="w-64 shrink-0">
      <div className="sticky top-6 space-y-3">
        <div className="rounded-xl border bg-card p-4 animate-pulse">
          <div className="h-2 bg-muted rounded w-1/3 mb-4" />
          {[1, 2, 3].map(i => (
            <div key={i} className="flex gap-2.5 mb-3">
              <div className="w-3 h-3 rounded-full bg-muted mt-3 shrink-0" />
              <div className="flex-1 space-y-1.5 pt-2">
                <div className="h-2 bg-muted rounded w-1/2" />
                <div className="h-2 bg-muted rounded w-3/4" />
                <div className="h-2 bg-muted rounded w-1/3" />
              </div>
            </div>
          ))}
        </div>
        <SkeletonBlock rows={2} />
        <SkeletonBlock rows={3} />
      </div>
    </aside>
  );

  if (error || !data || data.timeline.length === 0) return null;

  const { timeline, rapprochements, summary } = data;

  // ── Merge articles ────────────────────────────────────────────────────────
  const ficheArticles     = fiche?.articles     || [];
  const relationsArticles = relations?.articles || [];
  const { articles: smArticles, visaOnly } = mergeArticles(
    ficheArticles, relationsArticles, data.visa
  );
  const hasArticles = smArticles.length > 0 || visaOnly.length > 0;

  // ── Citations ─────────────────────────────────────────────────────────────
  const validRelations      = (relations?.fiche_id === ficheId) ? relations : null;
  const graphRapprochements = (validRelations?.procedure_graph ?? [])
    .filter(l => l.link_type === 'rapprochement' && l.resolved && l.target_slug);
  const ourCitations        = validRelations?.cite_arrets_qualifies || [];
  const { ours: topCitations, judilibreOnly } = mergeCitations(ourCitations, rapprochements);

  // Exclure les judilibreOnly déjà couverts par graphRapprochements
  const graphTargetNorms = new Set(
    graphRapprochements.flatMap(l => [
      normRef(l.target_number),
      normRef(l.target),
    ].filter(Boolean))
  );
  const judilibreOnlyFiltered = judilibreOnly.filter(r => {
    const num      = normRef(r.number);
    const numTitle = normRef(numFromTitle(r.title));
    return !graphTargetNorms.has(num) && !graphTargetNorms.has(numTitle);
  });

  const hasCitations = topCitations.length > 0
    || graphRapprochements.length > 0
    || judilibreOnlyFiltered.length > 0;

  return (
    <aside className="w-64 shrink-0">
      <div className="sticky top-6 space-y-3">

        {/* ── Timeline ────────────────────────────────────────────────────── */}
        <div className="rounded-xl border bg-card p-4">
          <h3 className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-4 flex items-center gap-1.5">
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            {t('fiche.timeline.chronology')}
          </h3>
          <div>
            {timeline.map((entry, i) => (
              <TimelineNode key={i} entry={entry} isLast={i === timeline.length - 1} />
            ))}
          </div>
          <p className="text-[9px] text-muted-foreground/40 mt-3 text-right">
            SmartLawyer Legal Graph
          </p>
        </div>

        {/* ── Sommaire ────────────────────────────────────────────────────── */}
        {summary && (
          <div className="rounded-xl border bg-card p-4">
            <h3 className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-2">
              {t('fiche.timeline.summary')}
            </h3>
            <p className="text-[11px] leading-relaxed text-muted-foreground line-clamp-6">
              {summary}
            </p>
          </div>
        )}

        {/* ── Articles visés ──────────────────────────────────────────────── */}
        {relationsLoading ? (
          <SkeletonBlock rows={3} />
        ) : hasArticles ? (
          <div className="rounded-xl border bg-card p-4">
            <h3 className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-3">
              {t('fiche.tab.articles')}
            </h3>
            <div className="space-y-1.5">
              {(showAllArticles ? smArticles : smArticles.slice(0, 6)).map((a, i) => {
                const path = articlePathFromFiche(a);
                const label = articleLabel(a.code, a.article);
                return path ? (
                  <Link
                    key={i}
                    to={path}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1.5 text-[11px] font-mono text-primary hover:text-primary/80 transition-colors"
                  >
                    <span className="text-[9px] text-muted-foreground/50">§</span>
                    {label}
                  </Link>
                ) : (
                  <div key={i} className="flex items-center gap-1.5">
                    <span className="text-[9px] text-muted-foreground/50">§</span>
                    <span className="text-[11px] font-mono text-muted-foreground line-clamp-1" title={label}>{label}</span>
                  </div>
                );
              })}
              {visaOnly.slice(0, 3).map((v, i) => {
                const label = v.numero && v.code_text
                  ? `art. ${v.numero} ${v.code_text}`
                  : v.title;
                return v.url ? (
                  <a key={`visa-${i}`} href={v.url} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-1.5 text-[11px] text-muted-foreground hover:text-foreground transition-colors">
                    <span className="text-[9px] text-muted-foreground/50">§</span>
                    <span className="line-clamp-1">{label}</span>
                  </a>
                ) : (
                  <div key={`visa-${i}`} className="flex items-center gap-1.5">
                    <span className="text-[9px] text-muted-foreground/50">§</span>
                    <p className="text-[11px] text-muted-foreground line-clamp-1">{label}</p>
                  </div>
                );
              })}
              {smArticles.length > 6 && (
                <button
                  onClick={() => setShowAllArticles(v => !v)}
                  className="text-[10px] text-primary/60 hover:text-primary transition-colors mt-1 text-left"
                >
                  {showAllArticles ? t('fiche.timeline.less') : t('fiche.timeline.more', { count: smArticles.length - 6 })}
                </button>
              )}
            </div>
          </div>
        ) : null}

        {/* ── Jurisprudence liée ──────────────────────────────────────────── */}
        {relationsLoading ? (
          <SkeletonBlock rows={4} />
        ) : hasCitations ? (
          <div className="rounded-xl border bg-card p-4">
            <h3 className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-3">
              {t('fiche.timeline.related')}
            </h3>
            <div className="space-y-2.5">

              {/* 1. Citations qualifiées LLM — prioritaires */}
              {topCitations.map((cite, i) => {
                const known = CITE_TYPE_LABEL[cite.type];
                const cfg = known
                  ? { label: t(known.labelKey), color: known.color }
                  : { label: cite.type, color: 'text-muted-foreground' };
                const inner = (
                  <div className="p-2 rounded-lg hover:bg-muted/60 transition-colors">
                    <div className="flex items-center gap-1.5 mb-0.5 flex-wrap">
                      <span className={`text-[9px] font-bold uppercase ${cfg.color}`}>
                        {cfg.label}
                      </span>
                      {cite.solution && (
                        <span className={`text-[9px] font-medium ${solutionColor(cite.solution)}`}>
                          · {cite.solution}
                        </span>
                      )}
                    </div>
                    <p className="text-[11px] font-mono text-foreground">{cite.ref}</p>
                    {cite.decision_date && (
                      <p className="text-[10px] text-muted-foreground">{formatDate(cite.decision_date)}</p>
                    )}
                    {cite.probleme && (
                      <p className="text-[10px] text-muted-foreground italic line-clamp-2 mt-0.5">
                        {cite.probleme}
                      </p>
                    )}
                  </div>
                );
                return cite.fiche_id ? (
                  <a key={i} href={`/jurisprudence/${(cite as any).slug}`} target="_blank" rel="noopener noreferrer">
                    {inner}
                  </a>
                ) : <div key={i}>{inner}</div>;
              })}

              {/* 2. Rapprochements résolus depuis procedure_graph — cliquables */}
              {graphRapprochements.map((link, i) => (
                <a key={`rapp-${i}`} href={`/jurisprudence/${link.target_slug}`} target="_blank" rel="noopener noreferrer">
                  <div className="p-2 rounded-lg hover:bg-muted/60 transition-colors">
                    <div className="flex items-center gap-1.5 mb-0.5">
                      <span className="text-[9px] font-bold uppercase text-purple-600 dark:text-purple-400">
                        {t('fiche.timeline.rapprochement')}
                      </span>
                      {link.target_solution && (
                        <span className={`text-[9px] font-medium ${solutionColor(link.target_solution)}`}>
                          · {link.target_solution}
                        </span>
                      )}
                    </div>
                    <p className="text-[11px] font-mono text-foreground">
                      {link.target_number || link.target}
                    </p>
                    {link.target_decision_date && (
                      <p className="text-[10px] text-muted-foreground">{formatDate(link.target_decision_date)}</p>
                    )}
                    {link.target_probleme && (
                      <p className="text-[10px] text-muted-foreground italic line-clamp-2 mt-0.5">
                        {link.target_probleme}
                      </p>
                    )}
                  </div>
                </a>
              ))}

              {/* 3. Rapprochements Judilibre — cliquables si résolus, sinon texte */}
              {judilibreOnlyFiltered.map((r, i) => {
                const displayNum = r.number || numFromTitle(r.title) || null;
                const inner = (
                  <div className={`p-2 rounded-lg hover:bg-muted/60 transition-colors ${!r.smartlawyer_id ? 'opacity-70' : ''}`}>
                    <div className="flex items-center gap-1.5 mb-0.5 flex-wrap">
                      <span className="text-[9px] text-muted-foreground uppercase">{t('fiche.timeline.similar')}</span>
                      {r.solution && (
                        <span className={`text-[9px] font-medium ${solutionColor(r.solution)}`}>
                          · {r.solution}
                        </span>
                      )}
                    </div>
                    {displayNum ? (
                      <p className="text-[11px] font-mono text-foreground">{displayNum}</p>
                    ) : (
                      <p className="text-[11px] text-foreground line-clamp-2">{r.title}</p>
                    )}
                    {r.date && (
                      <p className="text-[10px] text-muted-foreground">{formatDate(r.date)}</p>
                    )}
                    {!r.smartlawyer_id && (
                      <p className="text-[9px] text-muted-foreground/40 mt-0.5">{t('fiche.timeline.not_indexed')}</p>
                    )}
                  </div>
                );
                return r.smartlawyer_slug ? (
                  <a key={`jud-${i}`}
                     href={`/jurisprudence/${r.smartlawyer_slug}`}
                     target="_blank" rel="noopener noreferrer">
                    {inner}
                  </a>
                ) : <div key={`jud-${i}`}>{inner}</div>;
              })}

            </div>
          </div>
        ) : null}

      </div>
    </aside>
  );
}